import React from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Grid,
  GridItem,
  Label,
} from '@patternfly/react-core';
import type { Certificate, ZerosslOrder } from '../../../types/models/Certificate';
import { formatDateTime, formatRelativeDays } from '../../../utils/dateFormat';
import {
  CERTIFICATE_BUCKET_LABELS,
  CERTIFICATE_BUCKET_LABEL_COLORS,
  classifyCertificateBucket,
} from './certificateBuckets';
import { ZEROSSL_ORDER_STATE_LABELS, ZEROSSL_ORDER_STATE_LABEL_COLORS } from './zerosslOrderDisplay';
import { copyable, text } from './displayHelpers';

interface CertificateOverviewTabProps {
  certificate: Certificate;
  /** The Show endpoint's sibling — null/absent everywhere except ZeroSSL rows with an order. */
  zerosslOrder?: ZerosslOrder | null;
}

/**
 * Status badge for a certificate row. A non-issued ZeroSSL order overrides
 * the generic buckets (a pending/failed/cancelled order is all `status:
 * 'request'` underneath — "Pending requests" would hide the distinction);
 * issued orders fall through to the normal time-based buckets. The order can
 * ride on the row itself (list entries) or be passed explicitly (the Show
 * endpoint carries it as a sibling, not on the row).
 */
export function renderCertificateStatusLabel(certificate: Certificate, order?: ZerosslOrder | null): React.ReactNode {
  const effectiveOrder = order ?? certificate.zerossl_order ?? null;
  if (effectiveOrder !== null && effectiveOrder.state !== 'issued') {
    return (
      <Label color={ZEROSSL_ORDER_STATE_LABEL_COLORS[effectiveOrder.state]} isCompact>
        {ZEROSSL_ORDER_STATE_LABELS[effectiveOrder.state]}
      </Label>
    );
  }
  const bucket = classifyCertificateBucket(certificate);
  return (
    <Label color={CERTIFICATE_BUCKET_LABEL_COLORS[bucket]} isCompact>
      {CERTIFICATE_BUCKET_LABELS[bucket]}
    </Label>
  );
}

/**
 * Relative expiry hint shown next to the absolute "Valid to" timestamp.
 * Suppressed for revoked certificates (the revocation fields tell that
 * story) and when `not_after` is missing/unparseable.
 */
function expiryHint(certificate: Certificate): string | null {
  if (certificate.status === 'revoked' || certificate.revoked_at !== null || certificate.not_after === null) {
    return null;
  }
  const relative = formatRelativeDays(certificate.not_after);
  if (relative === null) {
    return null;
  }
  const expired = Date.parse(certificate.not_after) < Date.now();
  if (relative === 'today') {
    return expired ? 'expired today' : 'expires today';
  }
  return expired ? `expired ${relative}` : `expires ${relative}`;
}

function CertificateOverviewTab({ certificate, zerosslOrder = null }: CertificateOverviewTabProps) {
  const hint = expiryHint(certificate);

  return (
    <Grid hasGutter md={6} style={{ marginTop: '1rem' }}>
      <GridItem>
        <Card isFullHeight>
          <CardTitle>General</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Common name</DescriptionListTerm>
                <DescriptionListDescription>{certificate.cn}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Status</DescriptionListTerm>
                <DescriptionListDescription>
                  {renderCertificateStatusLabel(certificate, zerosslOrder)}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Alternative names</DescriptionListTerm>
                <DescriptionListDescription>
                  {certificate.sans.length === 0 ? (
                    '—'
                  ) : (
                    <div style={{ whiteSpace: 'pre-line' }}>{certificate.sans.join('\n')}</div>
                  )}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Issuer</DescriptionListTerm>
                <DescriptionListDescription>{text(certificate.issuer_cn)}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Platform reference</DescriptionListTerm>
                <DescriptionListDescription>
                  {copyable(certificate.platform_ref, 'platform reference')}
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>

      <GridItem>
        <Card isFullHeight>
          <CardTitle>Validity</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Valid from</DescriptionListTerm>
                <DescriptionListDescription>{formatDateTime(certificate.not_before)}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Valid to</DescriptionListTerm>
                <DescriptionListDescription>
                  {formatDateTime(certificate.not_after)}
                  {hint !== null && <> ({hint})</>}
                </DescriptionListDescription>
              </DescriptionListGroup>
              {certificate.revoked_at !== null && (
                <DescriptionListGroup>
                  <DescriptionListTerm>Revoked at</DescriptionListTerm>
                  <DescriptionListDescription>{formatDateTime(certificate.revoked_at)}</DescriptionListDescription>
                </DescriptionListGroup>
              )}
              {certificate.revocation_reason !== null && (
                <DescriptionListGroup>
                  <DescriptionListTerm>Revocation reason</DescriptionListTerm>
                  <DescriptionListDescription>{certificate.revocation_reason}</DescriptionListDescription>
                </DescriptionListGroup>
              )}
              <DescriptionListGroup>
                <DescriptionListTerm>Last synced</DescriptionListTerm>
                <DescriptionListDescription>{formatDateTime(certificate.last_synced_at)}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>

      <GridItem>
        <Card isFullHeight>
          <CardTitle>Keys &amp; identifiers</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Private key</DescriptionListTerm>
                <DescriptionListDescription>
                  {/* ZeroSSL keys never leave the Manager (it generates the
                      CSR and stores the key locally) — "on the platform"
                      would be wrong for them. */}
                  {certificate.has_private_key ? (
                    <Label color="green" isCompact>
                      {zerosslOrder !== null ? 'Stored in the Manager' : 'Stored on the platform'}
                    </Label>
                  ) : (
                    <Label color="grey" isCompact>
                      {zerosslOrder !== null ? 'Not available' : 'Not available on the platform'}
                    </Label>
                  )}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Serial</DescriptionListTerm>
                <DescriptionListDescription>{copyable(certificate.serial, 'serial number')}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>SHA-256 fingerprint</DescriptionListTerm>
                <DescriptionListDescription>
                  {copyable(certificate.fingerprint_sha256, 'SHA-256 fingerprint')}
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>

      {zerosslOrder !== null && (
        <GridItem>
          <Card isFullHeight>
            <CardTitle>ZeroSSL order</CardTitle>
            <CardBody>
              <DescriptionList isHorizontal isCompact>
                <DescriptionListGroup>
                  <DescriptionListTerm>State</DescriptionListTerm>
                  <DescriptionListDescription>
                    <Label color={ZEROSSL_ORDER_STATE_LABEL_COLORS[zerosslOrder.state]} isCompact>
                      {ZEROSSL_ORDER_STATE_LABELS[zerosslOrder.state]}
                    </Label>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Domains</DescriptionListTerm>
                  <DescriptionListDescription>
                    <div style={{ whiteSpace: 'pre-line' }}>{zerosslOrder.domains.join('\n')}</div>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Validity</DescriptionListTerm>
                  <DescriptionListDescription>{zerosslOrder.validity_days} days</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Key type</DescriptionListTerm>
                  <DescriptionListDescription>{zerosslOrder.key_type}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Auto-renew</DescriptionListTerm>
                  <DescriptionListDescription>
                    {zerosslOrder.auto_renew
                      ? 'Enabled — renews ~30 days before expiry when all domains use managed DNS'
                      : 'Disabled'}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                {zerosslOrder.renewed_from_certificate_id !== null && (
                  <DescriptionListGroup>
                    <DescriptionListTerm>Renewed from</DescriptionListTerm>
                    <DescriptionListDescription>
                      <Link to={`/datacenter/certificates/${zerosslOrder.renewed_from_certificate_id}`}>
                        Certificate #{zerosslOrder.renewed_from_certificate_id}
                      </Link>
                    </DescriptionListDescription>
                  </DescriptionListGroup>
                )}
                {zerosslOrder.last_error !== null && (
                  <DescriptionListGroup>
                    <DescriptionListTerm>Last error</DescriptionListTerm>
                    <DescriptionListDescription>{zerosslOrder.last_error}</DescriptionListDescription>
                  </DescriptionListGroup>
                )}
              </DescriptionList>
            </CardBody>
          </Card>
        </GridItem>
      )}
    </Grid>
  );
}

export default CertificateOverviewTab;
